import { DeviceMobile, AppleLogo, GooglePlayLogo, CheckCircle, ArrowRight } from "@phosphor-icons/react";

const EVERFIT_URL = "https://invite.everfit.io/Q9TTSYVZYA";
// Real QR via QR Server API pointing at the Everfit invite
const QR_URL = `https://api.qrserver.com/v1/create-qr-code/?size=480x480&margin=10&qzone=2&format=png&color=000000&bgcolor=ffffff&data=${encodeURIComponent(EVERFIT_URL)}`;

const features = [
  "Video demos for every exercise",
  "In-app messaging with your coach",
  "Calendar + workout tracking",
  "Custom macros & habit goals",
];

export default function Everfit() {
  return (
    <section
      id="everfit"
      data-testid="everfit-section"
      className="relative px-6 md:px-12 lg:px-20 py-16 lg:py-20 border-b border-white/10 overflow-hidden"
    >
      {/* Radial accent */}
      <div className="absolute -top-32 -right-32 w-[40rem] h-[40rem] rounded-full bg-[#FF1B91]/10 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[30rem] h-[30rem] rounded-full bg-[#FF1B91]/5 blur-[100px] pointer-events-none" />

      <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
        {/* Left - Copy */}
        <div className="lg:col-span-7">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#FF1B91]" />
            <p className="text-[11px] uppercase tracking-[0.4em] text-[#FF1B91] font-semibold">Join Everfit / 04</p>
          </div>
          <h2 className="font-display font-black uppercase text-4xl md:text-5xl lg:text-7xl leading-[0.88] tracking-tighter mb-8">
            Your coach.{" "}
            <span className="font-brush font-normal normal-case text-[#FF1B91] inline-block transform -rotate-2">in your pocket.</span>
          </h2>
          <p className="text-lg text-white/75 leading-relaxed max-w-xl mb-10">
            All Sweat With Slay programming lives inside the{" "}
            <span className="text-white font-semibold">Everfit app</span> — the same coaching platform trusted by thousands of athletes. Download, tap the invite link, and start training in under 60 seconds.
          </p>

          <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-4 mb-10 max-w-xl">
            {features.map((f) => (
              <li key={f} className="flex items-start gap-3 text-white/85">
                <CheckCircle size={20} weight="fill" className="text-[#FF1B91] flex-shrink-0 mt-0.5" />
                <span className="text-sm">{f}</span>
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap gap-4">
            <a
              href={EVERFIT_URL}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="everfit-cta-join"
              className="inline-flex items-center gap-3 bg-[#FF1B91] hover:bg-[#FF3FA3] text-white px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] btn-brutal btn-brutal-pink"
            >
              Join via Everfit <ArrowRight size={16} weight="bold" />
            </a>
            <a
              href="https://apps.apple.com/us/app/everfit-fitness-coaching/id1481756823"
              target="_blank"
              rel="noreferrer noopener"
              data-testid="everfit-cta-ios"
              className="inline-flex items-center gap-3 border border-white/30 hover:border-white text-white px-6 py-4 text-xs font-bold uppercase tracking-[0.2em]"
            >
              <AppleLogo size={18} weight="fill" /> iOS
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.everfit.app"
              target="_blank"
              rel="noreferrer noopener"
              data-testid="everfit-cta-android"
              className="inline-flex items-center gap-3 border border-white/30 hover:border-white text-white px-6 py-4 text-xs font-bold uppercase tracking-[0.2em]"
            >
              <GooglePlayLogo size={18} weight="fill" /> Android
            </a>
          </div>

          <p className="mt-6 text-[11px] uppercase tracking-[0.25em] text-white/40">
            Invite code: <span className="text-white font-bold font-mono">Q9TTSYVZYA</span>
          </p>
        </div>

        {/* Right - QR card */}
        <div className="lg:col-span-5">
          <div
            data-testid="everfit-app-mock"
            className="relative bg-white/[0.04] backdrop-blur-xl border border-white/10 p-8 lg:p-10"
          >
            <span className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-[#FF1B91]" />
            <span className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2 border-[#FF1B91]" />
            <span className="absolute bottom-0 left-0 w-3 h-3 border-b-2 border-l-2 border-[#FF1B91]" />
            <span className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-[#FF1B91]" />

            <div className="flex items-start justify-between mb-8">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">Scan to join</div>
                <div className="font-display font-black uppercase text-2xl mt-1">Everfit Invite</div>
              </div>
              <DeviceMobile size={28} weight="bold" className="text-[#FF1B91]" />
            </div>

            {/* Real QR */}
            <a
              href={EVERFIT_URL}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="everfit-qr-link"
              className="block bg-white p-5 mb-6 group"
            >
              <img
                src={QR_URL}
                alt="Scan to join Sweat With Slay on Everfit"
                className="w-full aspect-square object-contain group-hover:scale-[1.02] transition-transform"
              />
            </a>

            <div className="flex items-center justify-between text-xs">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">Code</div>
                <div className="font-mono text-white font-bold tracking-widest mt-1 text-sm">Q9TTSYVZYA</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">Status</div>
                <div className="font-display font-black uppercase text-[#FF1B91] mt-1">Active</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
